import { AdminPendingBusiness } from "../../../interfaces/AdminApprovalObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";
import { convertPendingBusiness } from "./onboardingConvertor";

// Admin projection of `pending_businesses`. Everything the captain-facing view
// already models is converted by convertPendingBusiness - this file only adds
// the review/audit columns on top, so there is exactly ONE null-guarding
// implementation of the shared fields.

const toNullableString = (value: unknown): string | null =>
  value === undefined || value === null || value === "" ? null : String(value);

const toNumber = (value: unknown): number => Number(value ?? 0) || 0;

export function convertAdminPendingBusiness(
  raw: Record<string, unknown>
): AdminPendingBusiness {
  return {
    ...convertPendingBusiness(raw),
    captainId: String(raw?.captainId ?? ""),
    nineSetupId: toNullableString(raw?.nineSetupId),
    payloadVersion: toNumber(raw?.payloadVersion),
    rejectionReason: toNullableString(raw?.rejectionReason),
    reviewedBy: toNullableString(raw?.reviewedBy),
    reviewedAt: toNullableString(raw?.reviewedAt),
  };
}

/**
 * contents of businesses/pending/list -> AdminPendingBusiness[].
 *
 * Shape-tolerant for the same reason convertPendingBusinessList is: this
 * backend's response builders have shipped rows both bare and wrapped.
 *  1. [ {pendingId, ...}, ... ]            - raw rows (the observed shape)
 *  2. { pendingBusinesses: [...] }         - wrapper object
 *  3. [ { pendingBusinesses: [...] } ]     - array-wrapped wrapper
 * Detection assumes `pendingId` is on every row and never on a wrapper.
 */
export function convertAdminPendingBusinessList(
  contents: unknown
): AdminPendingBusiness[] {
  if (Array.isArray(contents)) {
    const first = contents[0] as Record<string, unknown> | undefined;
    if (first && first.pendingId !== undefined) {
      return contents.map((row) =>
        convertAdminPendingBusiness(row as Record<string, unknown>)
      );
    }
  }
  const wrapper = coerceSingle(contents);
  const rows = wrapper.pendingBusinesses;
  if (!Array.isArray(rows)) return [];
  return rows.map((row) =>
    convertAdminPendingBusiness(row as Record<string, unknown>)
  );
}
